import {
  DIAGONAL_DIRECTIONS,
  isSquareAttacked,
  KING_OFFSETS,
  KNIGHT_OFFSETS,
  ORTHOGONAL_DIRECTIONS,
} from "./attacks";
import { cloneBoard, fileOf, findKing, isOnBoard, opponentOf, rankOf, squareAt } from "./board";
import type { GameState, Move, Piece, PieceType, Square } from "./types";

export const PROMOTION_PIECES: readonly Exclude<PieceType, "p" | "k">[] = ["q", "r", "b", "n"];

function pawnMoves(state: GameState, square: Square, piece: Piece): Move[] {
  const { board } = state;
  const file = fileOf(square);
  const rank = rankOf(square);
  const dir = piece.color === "w" ? 1 : -1;
  const startRank = piece.color === "w" ? 1 : 6;
  const promotionRank = piece.color === "w" ? 7 : 0;
  const moves: Move[] = [];

  const oneRank = rank + dir;
  if (isOnBoard(file, oneRank)) {
    const oneSquare = squareAt(file, oneRank);
    if (!board[oneSquare]) {
      if (oneRank === promotionRank) {
        for (const promotion of PROMOTION_PIECES) {
          moves.push({ from: square, to: oneSquare, piece, promotion });
        }
      } else {
        moves.push({ from: square, to: oneSquare, piece });
        if (rank === startRank) {
          const twoSquare = squareAt(file, rank + 2 * dir);
          if (!board[twoSquare]) {
            moves.push({ from: square, to: twoSquare, piece, setsEnPassant: oneSquare });
          }
        }
      }
    }
  }

  for (const fileDelta of [-1, 1]) {
    const f = file + fileDelta;
    const r = rank + dir;
    if (!isOnBoard(f, r)) continue;
    const target = squareAt(f, r);
    const targetPiece = board[target];
    if (targetPiece && targetPiece.color !== piece.color) {
      if (r === promotionRank) {
        for (const promotion of PROMOTION_PIECES) {
          moves.push({ from: square, to: target, piece, captured: targetPiece, promotion });
        }
      } else {
        moves.push({ from: square, to: target, piece, captured: targetPiece });
      }
    } else if (!targetPiece && target === state.enPassantSquare) {
      const capturedSquare = squareAt(f, rank);
      const capturedPawn = board[capturedSquare];
      if (capturedPawn) {
        moves.push({ from: square, to: target, piece, captured: capturedPawn, enPassantCapture: capturedSquare });
      }
    }
  }

  return moves;
}

function knightMoves(state: GameState, square: Square, piece: Piece): Move[] {
  const { board } = state;
  const file = fileOf(square);
  const rank = rankOf(square);
  const moves: Move[] = [];
  for (const [df, dr] of KNIGHT_OFFSETS) {
    const f = file + df;
    const r = rank + dr;
    if (!isOnBoard(f, r)) continue;
    const target = squareAt(f, r);
    const targetPiece = board[target];
    if (targetPiece && targetPiece.color === piece.color) continue;
    moves.push(targetPiece ? { from: square, to: target, piece, captured: targetPiece } : { from: square, to: target, piece });
  }
  return moves;
}

function kingStepMoves(state: GameState, square: Square, piece: Piece): Move[] {
  const { board } = state;
  const file = fileOf(square);
  const rank = rankOf(square);
  const moves: Move[] = [];
  for (const [df, dr] of KING_OFFSETS) {
    const f = file + df;
    const r = rank + dr;
    if (!isOnBoard(f, r)) continue;
    const target = squareAt(f, r);
    const targetPiece = board[target];
    if (targetPiece && targetPiece.color === piece.color) continue;
    moves.push(targetPiece ? { from: square, to: target, piece, captured: targetPiece } : { from: square, to: target, piece });
  }
  return moves;
}

function castlingMoves(state: GameState, square: Square, piece: Piece): Move[] {
  const { board } = state;
  const rank = piece.color === "w" ? 0 : 7;
  if (square !== squareAt(4, rank)) return [];
  const opponent = opponentOf(piece.color);
  const moves: Move[] = [];

  const kingsideRight = piece.color === "w" ? state.castling.wk : state.castling.bk;
  if (kingsideRight) {
    const fSquare = squareAt(5, rank);
    const gSquare = squareAt(6, rank);
    const rookSquare = squareAt(7, rank);
    const rook = board[rookSquare];
    if (
      !board[fSquare] &&
      !board[gSquare] &&
      rook?.type === "r" &&
      rook.color === piece.color &&
      !isSquareAttacked(board, square, opponent) &&
      !isSquareAttacked(board, fSquare, opponent) &&
      !isSquareAttacked(board, gSquare, opponent)
    ) {
      moves.push({ from: square, to: gSquare, piece, castle: "k" });
    }
  }

  const queensideRight = piece.color === "w" ? state.castling.wq : state.castling.bq;
  if (queensideRight) {
    const dSquare = squareAt(3, rank);
    const cSquare = squareAt(2, rank);
    const bSquare = squareAt(1, rank);
    const rookSquare = squareAt(0, rank);
    const rook = board[rookSquare];
    if (
      !board[dSquare] &&
      !board[cSquare] &&
      !board[bSquare] &&
      rook?.type === "r" &&
      rook.color === piece.color &&
      !isSquareAttacked(board, square, opponent) &&
      !isSquareAttacked(board, dSquare, opponent) &&
      !isSquareAttacked(board, cSquare, opponent)
    ) {
      moves.push({ from: square, to: cSquare, piece, castle: "q" });
    }
  }

  return moves;
}

function slidingMoves(state: GameState, square: Square, piece: Piece, directions: readonly [number, number][]): Move[] {
  const { board } = state;
  const file = fileOf(square);
  const rank = rankOf(square);
  const moves: Move[] = [];
  for (const [df, dr] of directions) {
    let f = file + df;
    let r = rank + dr;
    while (isOnBoard(f, r)) {
      const target = squareAt(f, r);
      const targetPiece = board[target];
      if (targetPiece) {
        if (targetPiece.color !== piece.color) {
          moves.push({ from: square, to: target, piece, captured: targetPiece });
        }
        break;
      }
      moves.push({ from: square, to: target, piece });
      f += df;
      r += dr;
    }
  }
  return moves;
}

/** Coups pseudo-legaux (ne verifie pas que son propre roi reste hors d'echec). */
export function pseudoLegalMovesFrom(state: GameState, square: Square): Move[] {
  const piece = state.board[square];
  if (!piece || piece.color !== state.turn) return [];
  switch (piece.type) {
    case "p":
      return pawnMoves(state, square, piece);
    case "n":
      return knightMoves(state, square, piece);
    case "b":
      return slidingMoves(state, square, piece, DIAGONAL_DIRECTIONS);
    case "r":
      return slidingMoves(state, square, piece, ORTHOGONAL_DIRECTIONS);
    case "q":
      return slidingMoves(state, square, piece, [...ORTHOGONAL_DIRECTIONS, ...DIAGONAL_DIRECTIONS]);
    case "k":
      return [...kingStepMoves(state, square, piece), ...castlingMoves(state, square, piece)];
  }
}

/** Applique un coup (suppose legal) et retourne le nouvel etat, sans muter `state`. */
export function applyMove(state: GameState, move: Move): GameState {
  const board = cloneBoard(state.board);
  const moving = board[move.from];
  if (!moving) throw new Error("Aucune piece sur la case de depart");

  board[move.from] = null;
  if (move.enPassantCapture !== undefined) board[move.enPassantCapture] = null;
  board[move.to] = move.promotion ? { type: move.promotion, color: moving.color } : moving;

  if (move.castle) {
    const rank = rankOf(move.from);
    const [rookFrom, rookTo] =
      move.castle === "k" ? [squareAt(7, rank), squareAt(5, rank)] : [squareAt(0, rank), squareAt(3, rank)];
    board[rookTo] = board[rookFrom] ?? null;
    board[rookFrom] = null;
  }

  let { wk, wq, bk, bq } = state.castling;
  if (moving.type === "k") {
    if (moving.color === "w") {
      wk = false;
      wq = false;
    } else {
      bk = false;
      bq = false;
    }
  }
  const touches = (sq: Square) => move.from === sq || move.to === sq;
  if (touches(squareAt(0, 0))) wq = false;
  if (touches(squareAt(7, 0))) wk = false;
  if (touches(squareAt(0, 7))) bq = false;
  if (touches(squareAt(7, 7))) bk = false;

  const halfmoveClock = moving.type === "p" || move.captured ? 0 : state.halfmoveClock + 1;
  const fullmoveNumber = state.turn === "b" ? state.fullmoveNumber + 1 : state.fullmoveNumber;

  return {
    board,
    turn: opponentOf(state.turn),
    castling: { wk, wq, bk, bq },
    enPassantSquare: move.setsEnPassant ?? null,
    halfmoveClock,
    fullmoveNumber,
  };
}

export function isInCheck(state: GameState, color = state.turn): boolean {
  const kingSquare = findKing(state.board, color);
  return isSquareAttacked(state.board, kingSquare, opponentOf(color));
}

/** Coups legaux depuis une case (le roi du joueur ne doit pas rester/etre mis en echec). */
export function legalMovesFrom(state: GameState, square: Square): Move[] {
  const piece = state.board[square];
  if (!piece) return [];
  return pseudoLegalMovesFrom(state, square).filter((move) => {
    const after = applyMove(state, move);
    return !isInCheck(after, piece.color);
  });
}

export function allLegalMoves(state: GameState): Move[] {
  const moves: Move[] = [];
  for (let square = 0; square < 64; square++) {
    const piece = state.board[square];
    if (piece && piece.color === state.turn) {
      moves.push(...legalMovesFrom(state, square));
    }
  }
  return moves;
}

/** Sous-ensemble des cases d'arrivee de `moves` qui capturent une piece (prise en passant incluse). */
export function captureSquaresOf(moves: readonly Move[]): Set<Square> {
  return new Set(moves.filter((m) => m.captured).map((m) => m.to));
}
